import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('login');
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [systemCapacity, setSystemCapacity] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showTopicGenerationModal, setShowTopicGenerationModal] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [generationResults, setGenerationResults] = useState(null);
  const [topicGenerationForm, setTopicGenerationForm] = useState({
    selectedProject: '',
    topic: '',
    targetLength: 'medium',
    qualityThreshold: 75
  });
  const [createProjectForm, setCreateProjectForm] = useState({
    name: '',
    targetUrl: '',
    postsPerDay: 5,
    minWordCount: 300
  });

  // Check for saved authentication on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem('seo_user');
    const savedToken = localStorage.getItem('seo_token');
    
    if (savedUser && savedToken) {
      try {
        const userData = JSON.parse(savedUser);
        setUser({ ...userData, token: savedToken });
        setCurrentView('dashboard');
        // Load system capacity and projects with saved token
        loadSystemCapacity(savedToken);
        loadProjects(savedToken);
      } catch (error) {
        // If parsing fails, clear invalid data
        localStorage.removeItem('seo_user');
        localStorage.removeItem('seo_token');
      }
    }
  }, []);

  const handleLogin = async (email, password) => {
    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('https://myseo2.aysaarsolutions.com/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const userData = { ...data.user, token: data.token };
        setUser(userData);
        setCurrentView('dashboard');
        setMessage('Login successful!');
        
        // Save authentication data to localStorage
        localStorage.setItem('seo_user', JSON.stringify(data.user));
        localStorage.setItem('seo_token', data.token);
        
        // Load system capacity and projects after login
        loadSystemCapacity(data.token);
        loadProjects(data.token);
      } else {
        setMessage('Error: ' + (data.error || 'Login failed'));
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
    
    setLoading(false);
  };

  const loadSystemCapacity = async (token) => {
    try {
      // Use real project data for stats
      const totalProjects = projects.length;
      const totalPostsNeeded = projects.reduce((sum, p) => sum + (p.posts_per_day || 5), 0);
      
      setSystemCapacity({
        capacity: {
          totalPbnSites: 0, // Will be updated when PBN sites are implemented
          totalCapacity: 125, // Default capacity
          postsUsedToday: 0,
          availableCapacity: 125,
          utilizationPercentage: 0,
          avgPostsPerSite: 0
        },
        projects: {
          totalProjects,
          totalPostsNeeded,
          avgPostsPerProject: totalProjects > 0 ? Math.round(totalPostsNeeded / totalProjects) : 0,
          demandVsCapacity: totalPostsNeeded > 0 ? Math.round((totalPostsNeeded / 125) * 100) : 0
        },
        todayExecution: {
          executionsToday: 0,
          postsCreatedToday: 0,
          backlinksCreatedToday: 0,
          avgQualityToday: 0
        }
      });
    } catch (error) {
      console.error('Failed to load system capacity:', error);
    }
  };

  const handleTokenExpired = () => {
    localStorage.removeItem('seo_user');
    localStorage.removeItem('seo_token');
    setUser(null);
    setCurrentView('login');
    setSystemCapacity(null);
    setMessage('Session expired. Please login again.');
  };

  const loadProjects = async (token) => {
    try {
      // Try regular projects API first (now enhanced with keyword data)
      let response = await fetch('https://myseo2.aysaarsolutions.com/projects.php', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // If regular API fails, try Core Engine API as fallback
      if (!response.ok) {
        response = await fetch('https://myseo2.aysaarsolutions.com/core-engine.php/projects', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get response text first to debug
      const responseText = await response.text();
      console.log('Raw response:', responseText.substring(0, 200));
      
      // Try to parse JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response text:', responseText);
        throw new Error('Invalid JSON response from server');
      }
      
      if (data.success) {
        // Ensure projects have the required fields
        const processedProjects = (data.projects || []).map(project => ({
          ...project,
          total_keywords: project.total_keywords || 0,
          batch_size: project.batch_size || 0,
          batchingStatus: (project.total_keywords || 0) > 0 ? 'configured' : 'needs_setup',
          rotationDays: (project.total_keywords || 0) > 0 ? Math.ceil((project.total_keywords || 0) / (project.posts_per_day || 5)) : 0
        }));
        setProjects(processedProjects);
      } else {
        throw new Error(data.error || 'Failed to load projects');
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setMessage('Failed to load projects. Please try again.');
    }
  };

  const handleManageKeywords = (project) => {
    setSelectedProject(project);
    setShowKeywordModal(true);
    // Load existing keywords if any
    if (project.total_keywords > 0) {
      loadProjectKeywords(project.id);
    }
  };

  const loadProjectKeywords = async (projectId) => {
    try {
      // Load keywords from the project's keywords JSON field
      const project = projects.find(p => p.id === projectId);
      if (project && project.keywords) {
        try {
          const keywords = JSON.parse(project.keywords);
          if (Array.isArray(keywords)) {
            setKeywordInput(keywords.join('\n'));
          }
        } catch (e) {
          console.error('Error parsing keywords:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load project keywords:', error);
    }
  };

  const saveProjectKeywords = async () => {
    if (!selectedProject || !keywordInput.trim()) {
      setMessage('Please enter at least one keyword');
      return;
    }

    const keywords = keywordInput.split('\n').map(k => k.trim()).filter(k => k.length > 0);
    
    if (keywords.length === 0) {
      setMessage('Please enter valid keywords');
      return;
    }

    setLoading(true);
    try {
      // Use regular projects API to update keywords
      const response = await fetch(`https://myseo2.aysaarsolutions.com/projects.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          id: selectedProject.id,
          keywords: keywords 
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage(`Keywords updated successfully! ${keywords.length} keywords saved.`);
        setShowKeywordModal(false);
        setKeywordInput('');
        setSelectedProject(null);
        // Reload projects to show updated status
        loadProjects(user.token);
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
    setLoading(false);
  };

  const createProject = async () => {
    if (!createProjectForm.name.trim() || !createProjectForm.targetUrl.trim()) {
      setMessage('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://myseo2.aysaarsolutions.com/projects.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: createProjectForm.name,
          targetUrl: createProjectForm.targetUrl,
          keywords: [], // Empty initially, will be added via keyword management
          minWordCount: createProjectForm.minWordCount,
          postsPerDay: createProjectForm.postsPerDay,
          postingMode: 'automatic',
          contentEngine: 'llm'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage('Project created successfully!');
        setShowCreateProjectModal(false);
        setCreateProjectForm({
          name: '',
          targetUrl: '',
          postsPerDay: 5,
          minWordCount: 300
        });
        // Reload projects to show new project
        loadProjects(user.token);
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
    setLoading(false);
  };

  // Topic-Based Content Generation Functions
  const executeProjectGeneration = async () => {
    if (projects.length === 0) {
      setMessage('No projects available for content generation. Please create a project first.');
      return;
    }

    setLoading(true);
    setMessage('Starting content generation...');
    
    try {
      const response = await fetch('https://myseo2.aysaarsolutions.com/core-engine.php/execute-daily-generation', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setGenerationResults(data);
        setMessage(`Content generation completed! ${data.successfulPosts || 0} posts created successfully.`);
        // Refresh system capacity to show updated stats
        loadSystemCapacity(user.token);
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
    setLoading(false);
  };

  const executeTopicGeneration = async () => {
    if (!topicGenerationForm.selectedProject || !topicGenerationForm.topic.trim()) {
      setMessage('Please select a project and enter a topic.');
      return;
    }

    setLoading(true);
    setMessage('Generating topic-based content...');
    
    try {
      const response = await fetch(`https://myseo2.aysaarsolutions.com/core-engine.php/execute-project/${topicGenerationForm.selectedProject}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic: topicGenerationForm.topic,
          targetLength: topicGenerationForm.targetLength,
          qualityThreshold: topicGenerationForm.qualityThreshold
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setGenerationResults(data);
        setMessage(`Topic content generated successfully! ${data.successfulPosts || 0} posts created.`);
        setShowTopicGenerationModal(false);
        setTopicGenerationForm({
          selectedProject: '',
          topic: '',
          targetLength: 'medium',
          qualityThreshold: 75
        });
        // Refresh system capacity
        loadSystemCapacity(user.token);
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
    setLoading(false);
  };

  const handleRegister = async (formData) => {
    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('https://myseo2.aysaarsolutions.com/register.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage('Registration successful! Your account is pending approval.');
        setCurrentView('login');
      } else {
        setMessage('Error: ' + (data.error || 'Registration failed'));
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
    
    setLoading(false);
  };

  if (currentView === 'login') {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>SEO Backlink Manager - Login</h2>
          
          {message && (
            <div className={message.includes('Error') ? 'error-message' : 'success-message'}>
              {message}
            </div>
          )}
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            handleLogin(formData.get('email'), formData.get('password'));
          }}>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                required
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                name="password"
                required
                placeholder="Enter your password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="auth-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          
          <p className="auth-switch">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => setCurrentView('register')}
              className="link-button"
            >
              Register
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (currentView === 'register') {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>Register for SEO Backlink Manager</h2>
          
          {message && (
            <div className={message.includes('Error') ? 'error-message' : 'success-message'}>
              {message}
            </div>
          )}
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            handleRegister({
              email: formData.get('email'),
              password: formData.get('password'),
              companyName: formData.get('companyName')
            });
          }}>
            <div className="form-group">
              <label>Company Name:</label>
              <input
                type="text"
                name="companyName"
                required
                placeholder="Enter company name"
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                required
                placeholder="Enter email"
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                name="password"
                required
                placeholder="Enter password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="auth-button"
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
          
          <p className="auth-switch">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => setCurrentView('login')}
              className="link-button"
            >
              Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>SEO Backlink Manager</h1>
          <div className="user-info">
            <span>Welcome, {user?.email}</span>
            {user?.role === 'superadmin' && <span className="admin-badge">SUPER ADMIN</span>}
            <button 
              onClick={() => {
                // Clear authentication data from localStorage
                localStorage.removeItem('seo_user');
                localStorage.removeItem('seo_token');
                setUser(null);
                setCurrentView('login');
                setSystemCapacity(null);
                setMessage('Logged out successfully');
              }} 
              className="logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      
      {message && (
        <div className={message.includes('Error') ? 'error-message' : 'success-message'} style={{ margin: '10px 20px' }}>
          {message}
        </div>
      )}
      
      <div className="dashboard-content">
        <nav className="sidebar">
          <ul className="nav-menu">
            <li 
              className={activeTab === 'overview' ? 'active' : ''}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </li>
            <li 
              className={activeTab === 'projects' ? 'active' : ''}
              onClick={() => setActiveTab('projects')}
            >
              Projects
            </li>
            <li 
              className={activeTab === 'pbn-sites' ? 'active' : ''}
              onClick={() => setActiveTab('pbn-sites')}
            >
              PBN Sites
            </li>
            <li 
              className={activeTab === 'core-engine' ? 'active' : ''}
              onClick={() => setActiveTab('core-engine')}
            >
              Core Engine
            </li>
            <li 
              className={activeTab === 'llm-config' ? 'active' : ''}
              onClick={() => setActiveTab('llm-config')}
            >
              LLM Config
            </li>
            <li 
              className={activeTab === 'analytics' ? 'active' : ''}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </li>
            {user?.role === 'superadmin' && (
              <li 
                className={activeTab === 'admin' ? 'active' : ''}
                onClick={() => setActiveTab('admin')}
              >
                Admin Panel
              </li>
            )}
          </ul>
        </nav>
        
        <main className="main-content">
          {activeTab === 'overview' && (
            <div className="tab-content">
              <h3>Dashboard Overview</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <h4>Projects</h4>
                  <div className="stat-number">{systemCapacity?.projects?.totalProjects || 0}</div>
                  <p>Active SEO campaigns</p>
                </div>
                <div className="stat-card">
                  <h4>PBN Sites</h4>
                  <div className="stat-number">{systemCapacity?.capacity?.totalPbnSites || 0}</div>
                  <p>Connected WordPress sites</p>
                </div>
                <div className="stat-card">
                  <h4>Daily Capacity</h4>
                  <div className="stat-number">{systemCapacity?.capacity?.totalCapacity || 0}</div>
                  <p>Posts per day capacity</p>
                </div>
                <div className="stat-card">
                  <h4>Utilization</h4>
                  <div className="stat-number">{systemCapacity?.capacity?.utilizationPercentage || 0}%</div>
                  <p>Current usage</p>
                </div>
              </div>
              <p style={{ marginTop: '20px', padding: '20px', background: '#f0f8ff', border: '1px solid #0066cc', borderRadius: '5px' }}>
                🎉 <strong>Core Engine Ready!</strong> Your SEO Backlink Manager now includes intelligent content generation with quality control and automated posting.
              </p>
            </div>
          )}

          {activeTab === 'core-engine' && (
            <div className="tab-content">
              <h3>Core Engine Dashboard</h3>
              
              {systemCapacity && (
                <>
                  <div className="core-engine-status">
                    <h4>System Capacity Overview</h4>
                    <div className="capacity-grid">
                      <div className="capacity-card">
                        <h5>PBN Sites</h5>
                        <div className="capacity-number">{systemCapacity.capacity.totalPbnSites}</div>
                        <p>Active sites</p>
                      </div>
                      <div className="capacity-card">
                        <h5>Daily Capacity</h5>
                        <div className="capacity-number">{systemCapacity.capacity.totalCapacity}</div>
                        <p>Posts per day</p>
                      </div>
                      <div className="capacity-card">
                        <h5>Available</h5>
                        <div className="capacity-number">{systemCapacity.capacity.availableCapacity}</div>
                        <p>Unused capacity</p>
                      </div>
                      <div className="capacity-card">
                        <h5>Utilization</h5>
                        <div className="capacity-number">{systemCapacity.capacity.utilizationPercentage}%</div>
                        <p>Current usage</p>
                      </div>
                    </div>
                  </div>

                  <div className="core-engine-actions">
                    <h4>Topic-Based Content Generation</h4>
                    <div className="action-buttons">
                      <button 
                        className="action-button primary"
                        onClick={() => setShowTopicGenerationModal(true)}
                      >
                        🎯 Generate Topic Content
                      </button>
                      <button 
                        className="action-button secondary"
                        onClick={() => executeProjectGeneration()}
                        disabled={loading}
                      >
                        {loading ? '⏳ Generating...' : '🚀 Execute Daily Generation'}
                      </button>
                      <button 
                        className="action-button secondary"
                        onClick={() => loadSystemCapacity(user.token)}
                      >
                        🔄 Refresh Capacity
                      </button>
                    </div>
                  </div>

                  <div className="topic-generation-status">
                    <h4>Generation Status</h4>
                    {generationResults && (
                      <div className="generation-results">
                        <div className="result-summary">
                          <div className="result-item success">
                            <span className="result-label">Successful Posts:</span>
                            <span className="result-value">{generationResults.successfulPosts || 0}</span>
                          </div>
                          <div className="result-item failed">
                            <span className="result-label">Failed Posts:</span>
                            <span className="result-value">{generationResults.failedPosts || 0}</span>
                          </div>
                          <div className="result-item quality">
                            <span className="result-label">Avg Quality Score:</span>
                            <span className="result-value">{generationResults.avgQualityScore || 0}%</span>
                          </div>
                        </div>
                        
                        {generationResults.results && generationResults.results.length > 0 && (
                          <div className="detailed-results">
                            <h5>Recent Generation Results:</h5>
                            <div className="results-list">
                              {generationResults.results.slice(0, 5).map((result, index) => (
                                <div key={index} className={`result-card ${result.success ? 'success' : 'failed'}`}>
                                  <div className="result-header">
                                    <span className="result-keyword">{result.keyword}</span>
                                    <span className={`result-status ${result.success ? 'success' : 'failed'}`}>
                                      {result.success ? '✅' : '❌'}
                                    </span>
                                  </div>
                                  {result.success && (
                                    <div className="result-details">
                                      <span>Quality: {result.qualityScore}%</span>
                                      <span>Words: {result.wordCount}</span>
                                      <span>Backlinks: {result.backlinkCount}</span>
                                    </div>
                                  )}
                                  {!result.success && (
                                    <div className="result-error">
                                      <span>{result.reason}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {!generationResults && (
                      <div className="no-results">
                        <p>No recent generation results. Click "Execute Daily Generation" to start creating content.</p>
                      </div>
                    )}
                  </div>

                  <div className="core-engine-info">
                    <h4>System Status</h4>
                    <div className="status-grid">
                      <div className="status-item">
                        <span className="status-label">Projects Needing Content:</span>
                        <span className="status-value">{systemCapacity.projects.totalProjects}</span>
                      </div>
                      <div className="status-item">
                        <span className="status-label">Daily Posts Needed:</span>
                        <span className="status-value">{systemCapacity.projects.totalPostsNeeded}</span>
                      </div>
                      <div className="status-item">
                        <span className="status-label">Scaling Headroom:</span>
                        <span className="status-value">{Math.floor(systemCapacity.capacity.availableCapacity / 5)} more projects</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!systemCapacity && (
                <div className="loading-state">
                  <p>Loading Core Engine status...</p>
                  <button onClick={() => loadSystemCapacity(user.token)}>Retry</button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="tab-content">
              <div className="projects-header">
                <h3>Projects Management</h3>
                <button 
                  className="action-button primary"
                  onClick={() => setShowCreateProjectModal(true)}
                >
                  + Create New Project
                </button>
              </div>
              
              {projects.length > 0 ? (
                <div className="projects-grid">
                  {projects.map(project => (
                    <div key={project.id} className="project-card">
                      <div className="project-header">
                        <h4>{project.name}</h4>
                        <span className={`status-badge ${project.batchingStatus}`}>
                          {project.batchingStatus === 'configured' ? 'Keywords Ready' : 'Needs Setup'}
                        </span>
                      </div>
                      
                      <div className="project-stats">
                        <div className="stat-item">
                          <span className="label">Target URL:</span>
                          <span className="value" title={project.target_url}>
                            {project.target_url ? project.target_url.substring(0, 30) + '...' : 'Not set'}
                          </span>
                        </div>
                        <div className="stat-item">
                          <span className="label">Posts/Day:</span>
                          <span className="value">{project.posts_per_day || 5}</span>
                        </div>
                        <div className="stat-item">
                          <span className="label">Keywords:</span>
                          <span className="value">{project.total_keywords}</span>
                        </div>
                        {project.total_keywords > 0 && (
                          <>
                            <div className="stat-item">
                              <span className="label">Batch Size:</span>
                              <span className="value">{project.batch_size}</span>
                            </div>
                            <div className="stat-item">
                              <span className="label">Rotation:</span>
                              <span className="value">Every {project.rotationDays} day(s)</span>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="project-actions">
                        <button 
                          className="action-button primary"
                          onClick={() => handleManageKeywords(project)}
                        >
                          {project.total_keywords > 0 ? 'Edit Keywords' : 'Setup Keywords'}
                        </button>
                        <button 
                          className="action-button secondary"
                          onClick={() => {
                            setMessage('Edit project functionality coming soon!');
                            setTimeout(() => setMessage(''), 3000);
                          }}
                        >
                          Edit Project
                        </button>
                        <button 
                          className="action-button secondary"
                          onClick={() => {
                            setMessage('Pause/Resume functionality coming soon!');
                            setTimeout(() => setMessage(''), 3000);
                          }}
                        >
                          {project.status === 'active' ? 'Pause' : 'Resume'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No projects found. Create your first project to get started!</p>
                  <button 
                    className="action-button secondary"
                    onClick={() => loadProjects(user.token)}
                  >
                    Refresh Projects
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Keyword Management Modal */}
          {showKeywordModal && selectedProject && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Manage Keywords - {selectedProject.name}</h3>
                  <button 
                    className="close-button"
                    onClick={() => {
                      setShowKeywordModal(false);
                      setKeywordInput('');
                      setSelectedProject(null);
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="project-info">
                    <p><strong>Posts per day:</strong> {selectedProject.posts_per_day || 5}</p>
                    <p><strong>Current keywords:</strong> {selectedProject.total_keywords}</p>
                  </div>
                  
                  <div className="keyword-input-section">
                    <label>Keywords (one per line):</label>
                    <textarea
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      placeholder="Enter keywords, one per line:&#10;SEO optimization&#10;backlink building&#10;content marketing&#10;digital marketing"
                      rows="10"
                      className="keyword-textarea"
                    />
                    <p className="input-help">
                      Enter one keyword per line. The system will automatically calculate optimal batching based on your project's posts per day setting.
                    </p>
                  </div>
                  
                  {keywordInput.trim() && (
                    <div className="batching-preview">
                      <h4>Smart Batching Preview:</h4>
                      {(() => {
                        const keywords = keywordInput.split('\n').filter(k => k.trim().length > 0);
                        const postsPerDay = selectedProject.posts_per_day || 5;
                        const batchSize = Math.min(postsPerDay, keywords.length);
                        const totalBatches = Math.ceil(keywords.length / batchSize);
                        const rotationDays = Math.ceil(keywords.length / postsPerDay);
                        
                        return (
                          <div className="preview-stats">
                            <div className="preview-item">
                              <span>Total Keywords:</span> <strong>{keywords.length}</strong>
                            </div>
                            <div className="preview-item">
                              <span>Batch Size:</span> <strong>{batchSize} keywords per batch</strong>
                            </div>
                            <div className="preview-item">
                              <span>Total Batches:</span> <strong>{totalBatches}</strong>
                            </div>
                            <div className="preview-item">
                              <span>Rotation:</span> <strong>Every {rotationDays} day(s)</strong>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
                
                <div className="modal-footer">
                  <button 
                    className="action-button secondary"
                    onClick={() => {
                      setShowKeywordModal(false);
                      setKeywordInput('');
                      setSelectedProject(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="action-button primary"
                    onClick={saveProjectKeywords}
                    disabled={loading || !keywordInput.trim()}
                  >
                    {loading ? 'Saving...' : 'Save Keywords'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Create Project Modal */}
          {showCreateProjectModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Create New Project</h3>
                  <button 
                    className="close-button"
                    onClick={() => {
                      setShowCreateProjectModal(false);
                      setCreateProjectForm({
                        name: '',
                        targetUrl: '',
                        postsPerDay: 5,
                        minWordCount: 300
                      });
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="form-group">
                    <label>Project Name *</label>
                    <input
                      type="text"
                      value={createProjectForm.name}
                      onChange={(e) => setCreateProjectForm({...createProjectForm, name: e.target.value})}
                      placeholder="Enter project name (e.g., My SEO Campaign)"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Target URL *</label>
                    <input
                      type="url"
                      value={createProjectForm.targetUrl}
                      onChange={(e) => setCreateProjectForm({...createProjectForm, targetUrl: e.target.value})}
                      placeholder="https://example.com"
                    />
                    <p className="input-help">The website you want to build backlinks for</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Posts Per Day</label>
                    <select
                      value={createProjectForm.postsPerDay}
                      onChange={(e) => setCreateProjectForm({...createProjectForm, postsPerDay: parseInt(e.target.value)})}
                    >
                      <option value={1}>1 post per day</option>
                      <option value={2}>2 posts per day</option>
                      <option value={3}>3 posts per day</option>
                      <option value={4}>4 posts per day</option>
                      <option value={5}>5 posts per day</option>
                      <option value={6}>6 posts per day</option>
                      <option value={7}>7 posts per day</option>
                      <option value={8}>8 posts per day</option>
                      <option value={9}>9 posts per day</option>
                      <option value={10}>10 posts per day</option>
                    </select>
                    <p className="input-help">How many backlink posts to create daily for this project</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Minimum Word Count</label>
                    <select
                      value={createProjectForm.minWordCount}
                      onChange={(e) => setCreateProjectForm({...createProjectForm, minWordCount: parseInt(e.target.value)})}
                    >
                      <option value={300}>300 words</option>
                      <option value={500}>500 words</option>
                      <option value={750}>750 words</option>
                      <option value={1000}>1000 words</option>
                      <option value={1500}>1500 words</option>
                    </select>
                    <p className="input-help">Minimum article length for generated content</p>
                  </div>
                  
                  <div className="project-info">
                    <p><strong>Note:</strong> After creating the project, you'll be able to add keywords and configure intelligent batching based on your posts per day setting.</p>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    className="action-button secondary"
                    onClick={() => {
                      setShowCreateProjectModal(false);
                      setCreateProjectForm({
                        name: '',
                        targetUrl: '',
                        postsPerDay: 5,
                        minWordCount: 300
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="action-button primary"
                    onClick={createProject}
                    disabled={loading || !createProjectForm.name.trim() || !createProjectForm.targetUrl.trim()}
                  >
                    {loading ? 'Creating...' : 'Create Project'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Topic Generation Modal */}
          {showTopicGenerationModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Generate Topic-Based Content</h3>
                  <button 
                    className="close-button"
                    onClick={() => {
                      setShowTopicGenerationModal(false);
                      setTopicGenerationForm({
                        selectedProject: '',
                        topic: '',
                        targetLength: 'medium',
                        qualityThreshold: 75
                      });
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={topicGenerationForm.selectedProject}
                      onChange={(e) => setTopicGenerationForm({...topicGenerationForm, selectedProject: e.target.value})}
                    >
                      <option value="">Choose a project...</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name} ({project.total_keywords} keywords)
                        </option>
                      ))}
                    </select>
                    <p className="input-help">Select which project to generate content for</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Content Topic *</label>
                    <input
                      type="text"
                      value={topicGenerationForm.topic}
                      onChange={(e) => setTopicGenerationForm({...topicGenerationForm, topic: e.target.value})}
                      placeholder="e.g., Best SEO Practices for Small Business"
                    />
                    <p className="input-help">The main topic for content generation</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Target Content Length</label>
                    <select
                      value={topicGenerationForm.targetLength}
                      onChange={(e) => setTopicGenerationForm({...topicGenerationForm, targetLength: e.target.value})}
                    >
                      <option value="short">Short (300-500 words) - 1 backlink</option>
                      <option value="medium">Medium (500-1000 words) - 2 backlinks</option>
                      <option value="long">Long (1000-1500 words) - 3 backlinks</option>
                    </select>
                    <p className="input-help">Longer content gets more backlinks naturally</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Quality Threshold</label>
                    <select
                      value={topicGenerationForm.qualityThreshold}
                      onChange={(e) => setTopicGenerationForm({...topicGenerationForm, qualityThreshold: parseInt(e.target.value)})}
                    >
                      <option value={60}>60% - Basic Quality</option>
                      <option value={70}>70% - Good Quality</option>
                      <option value={75}>75% - High Quality (Recommended)</option>
                      <option value={80}>80% - Premium Quality</option>
                      <option value={85}>85% - Exceptional Quality</option>
                    </select>
                    <p className="input-help">Only content above this score will be published</p>
                  </div>
                  
                  <div className="generation-preview">
                    <h4>Generation Preview</h4>
                    {topicGenerationForm.selectedProject && topicGenerationForm.topic && (
                      <div className="preview-stats">
                        <div className="preview-item">
                          <span>Project:</span> <strong>{projects.find(p => p.id == topicGenerationForm.selectedProject)?.name}</strong>
                        </div>
                        <div className="preview-item">
                          <span>Topic:</span> <strong>{topicGenerationForm.topic}</strong>
                        </div>
                        <div className="preview-item">
                          <span>Expected Length:</span> <strong>{topicGenerationForm.targetLength}</strong>
                        </div>
                        <div className="preview-item">
                          <span>Quality Gate:</span> <strong>{topicGenerationForm.qualityThreshold}%</strong>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    className="action-button secondary"
                    onClick={() => {
                      setShowTopicGenerationModal(false);
                      setTopicGenerationForm({
                        selectedProject: '',
                        topic: '',
                        targetLength: 'medium',
                        qualityThreshold: 75
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="action-button primary"
                    onClick={executeTopicGeneration}
                    disabled={loading || !topicGenerationForm.selectedProject || !topicGenerationForm.topic.trim()}
                  >
                    {loading ? 'Generating...' : 'Generate Content'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pbn-sites' && (
            <div className="tab-content">
              <h3>PBN Sites Management</h3>
              <p>PBN site management interface available.</p>
            </div>
          )}

          {activeTab === 'llm-config' && (
            <div className="tab-content">
              <h3>LLM Configuration</h3>
              <p>GROQ API key management coming in Sprint 3!</p>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="tab-content">
              <h3>Analytics Dashboard</h3>
              <p>Analytics and reporting interface available.</p>
            </div>
          )}

          {activeTab === 'admin' && user?.role === 'superadmin' && (
            <div className="tab-content">
              <h3>Admin Panel</h3>
              <p>Super admin controls available.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;